import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import {
  reactTableNaturalSort,
  reactTableNaturalSortReactNodes,
} from '../../../../generic/Table/reactTableNaturalSort'
import usePersistUserTablePreferences from '../../../../generic/Table/usePersistUserTablePreferences'
import { useCurrentUser } from '../../../../../App/CurrentUserContext'
import { splitSearchQueryStrings } from '../../../../../library/splitSearchQueryStrings'
import { getTableFilteredRows } from '../../../../../library/getTableFilteredRows'
import { useGlobalFilter, usePagination, useSortBy, useTable } from 'react-table'
import { PAGE_SIZE_DEFAULT } from '../../../../../library/constants/constants'
import { StyledToolbarButtonWrapper } from '../../Gfcr/Gfcr.styles'
import { IconPlus } from '../../../../icons'
import {
  ButtonSecondary,
  ButtonThatLooksLikeLinkUnderlined,
  ToolbarButtonWrapper,
} from '../../../../generic/buttons'
import PageUnavailable from '../../../PageUnavailable'
import { ToolBarRow } from '../../../../generic/positioning'
import FilterSearchToolbar from '../../../../FilterSearchToolbar/FilterSearchToolbar'
import {
  TableContentToolbar,
  StyledTableContentWrapper,
  StyledTableAnchor,
} from './subPages.styles'
import { choicesPropType } from '../../../../../App/mermaidData/mermaidDataProptypes'
import { MuiTooltip } from '../../../../generic/MuiTooltip'
import GfcrGenericTable from '../../GfcrGenericTable'
import IconCheckLabel from './IconCheckLabel'
import RevenueModal from '../modals/RevenueModal'
import formattedCurrencyAmount from '../../../../../library/formatCurrencyAmount'
import { useTranslation, Trans } from 'react-i18next'

const Revenues = ({ indicatorSet, setIndicatorSet, choices, setSelectedNavItem, displayHelp }) => {
  const { t } = useTranslation()

  const businessFinanceSolutionHeaderText = t(
    'gfcr.forms.finance_solutions.business_finance_solution',
  )
  const revenueTypeHeaderText = t('gfcr.forms.revenues.revenue_type')
  const sustainableRevenueStreamHeaderText = t('gfcr.forms.revenues.sustainable_revenue_stream')
  const revenueAmountHeaderText = t('gfcr.forms.revenues.revenue_amount')

  const { currentUser } = useCurrentUser()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [revenueBeingEdited, setRevenueBeingEdited] = useState()

  const revenues = useMemo(() => {
    return indicatorSet.finance_solutions.flatMap((fs) => fs.revenues)
  }, [indicatorSet.finance_solutions])

  const nonPcfFinanceSolutions = useMemo(
    () => indicatorSet.finance_solutions.filter((fs) => fs.fs_type !== 'programmatic_co_financing'),
    [indicatorSet.finance_solutions],
  )

  const addRevenueDisabled = !nonPcfFinanceSolutions.length

  const tableColumns = useMemo(
    () => [
      {
        Header: businessFinanceSolutionHeaderText,
        accessor: 'finance_solution',
        sortType: reactTableNaturalSortReactNodes,
      },
      {
        Header: revenueTypeHeaderText,
        accessor: 'revenue_type',
        sortType: reactTableNaturalSort,
      },
      {
        Header: sustainableRevenueStreamHeaderText,
        accessor: 'sustainable_revenue_stream',
        sortType: reactTableNaturalSort,
        align: 'center',
      },
      {
        Header: revenueAmountHeaderText,
        accessor: 'revenue_amount',
        sortType: reactTableNaturalSort,
        align: 'right',
      },
    ],
    [
      businessFinanceSolutionHeaderText,
      revenueTypeHeaderText,
      sustainableRevenueStreamHeaderText,
      revenueAmountHeaderText,
    ],
  )

  const handleEditRevenue = useCallback(
    (event) => {
      event.preventDefault()
      const revenue = revenues.find((rev) => rev.id === event.target.id)

      setRevenueBeingEdited(revenue)
      setIsModalOpen(true)
    },
    [revenues],
  )

  const tableCellData = useMemo(() => {
    if (!choices || !revenues) {
      return
    }

    // eslint-disable-next-line consistent-return
    return revenues.map((revenue) => {
      const { id, finance_solution, revenue_type, sustainable_revenue_stream, revenue_amount } =
        revenue

      const revenueTypeName = choices.revenuetypes.data?.find(
        (revenueTypeChoice) => revenueTypeChoice.id === revenue_type,
      ).name

      const formattedRevenueAmount = formattedCurrencyAmount(revenue_amount)

      return {
        finance_solution: (
          <StyledTableAnchor id={id} onClick={(event) => handleEditRevenue(event)}>
            {indicatorSet.finance_solutions.find((fs) => fs.id === finance_solution).name}
          </StyledTableAnchor>
        ),
        revenue_type: revenueTypeName,
        sustainable_revenue_stream: <IconCheckLabel isCheck={!!sustainable_revenue_stream} />,
        revenue_amount: `${formattedRevenueAmount}`,
      }
    })
  }, [choices, handleEditRevenue, indicatorSet.finance_solutions, revenues])

  const tableDefaultPrefs = useMemo(() => {
    return {
      sortBy: [
        {
          id: 'finance_solution',
          desc: false,
        },
      ],
      globalFilter: '',
    }
  }, [])

  const [tableUserPrefs, handleSetTableUserPrefs] = usePersistUserTablePreferences({
    key: `${currentUser && currentUser.id}-gfcrRevenuesTable`,
    defaultValue: tableDefaultPrefs,
  })

  const tableGlobalFilters = useCallback((rows, id, query) => {
    const keys = [
      'values.finance_solution.props.children',
      'values.revenue_type',
      'values.sustainable_revenue_stream',
      'values.revenue_amount',
    ]

    const queryTerms = splitSearchQueryStrings(query)

    return !queryTerms || !queryTerms.length ? rows : getTableFilteredRows(rows, keys, queryTerms)
  }, [])

  const {
    canNextPage,
    canPreviousPage,
    getTableBodyProps,
    getTableProps,
    gotoPage,
    headerGroups,
    nextPage,
    page,
    pageOptions,
    prepareRow,
    previousPage,
    rows,
    setPageSize,
    state: { pageIndex, pageSize, sortBy, globalFilter },
    setGlobalFilter,
  } = useTable(
    {
      columns: tableColumns,
      data: tableCellData,
      initialState: {
        pageSize: tableUserPrefs.pageSize ? tableUserPrefs.pageSize : PAGE_SIZE_DEFAULT,
        sortBy: tableUserPrefs.sortBy,
        globalFilter: tableUserPrefs.globalFilter,
      },
      globalFilter: tableGlobalFilters,
      // Disables requirement to hold shift to enable multi-sort
      isMultiSortEvent: () => true,
    },
    useGlobalFilter,
    useSortBy,
    usePagination,
  )

  const handleRowsNumberChange = (e) => {
    setPageSize(Number(e.target.value))
  }

  const handleGlobalFilterChange = (value) => setGlobalFilter(value)

  const _setSortByPrefs = useEffect(() => {
    handleSetTableUserPrefs({ propertyKey: 'sortBy', currentValue: sortBy })
  }, [sortBy, handleSetTableUserPrefs])

  const _setFilterPrefs = useEffect(() => {
    handleSetTableUserPrefs({ propertyKey: 'globalFilter', currentValue: globalFilter })
  }, [globalFilter, handleSetTableUserPrefs])

  const _setPageSizePrefs = useEffect(() => {
    handleSetTableUserPrefs({ propertyKey: 'pageSize', currentValue: pageSize })
  }, [pageSize, handleSetTableUserPrefs])

  const handleAddRevenue = (event) => {
    event.preventDefault()
    setIsModalOpen(true)
  }

  const handleRevenueModalDismiss = (resetForm) => {
    resetForm()
    setRevenueBeingEdited()
    setIsModalOpen(false)
  }

  const toolbarButtons = (
    <>
      <StyledToolbarButtonWrapper>
        <MuiTooltip
          title={
            addRevenueDisabled && indicatorSet.finance_solutions.length > 0
              ? t('gfcr.forms.revenues.no_non_pcf_finance_solutions')
              : ''
          }
        >
          <span>
            <ButtonSecondary
              onClick={(event) => handleAddRevenue(event)}
              disabled={addRevenueDisabled}
              style={addRevenueDisabled ? { pointerEvents: 'none' } : undefined}
            >
              <IconPlus /> {t('gfcr.forms.revenues.add')}
            </ButtonSecondary>
          </span>
        </MuiTooltip>
      </StyledToolbarButtonWrapper>
    </>
  )

  const table = revenues.length ? (
    <GfcrGenericTable
      getTableProps={getTableProps}
      headerGroups={headerGroups}
      getTableBodyProps={getTableBodyProps}
      page={page}
      prepareRow={prepareRow}
      onPageSizeChange={handleRowsNumberChange}
      pageSize={pageSize}
      unfilteredRowLength={revenues.length}
      searchFilteredRowsLength={rows.length}
      isSearchFilterEnabled={!!globalFilter?.length}
      onPreviousClick={previousPage}
      previousDisabled={!canPreviousPage}
      onNextClick={nextPage}
      nextDisabled={!canNextPage}
      onGoToPage={gotoPage}
      currentPageIndex={pageIndex}
      pageCount={pageOptions.length}
    />
  ) : (
    <PageUnavailable
      mainText={t('gfcr.forms.revenues.no_revenues')}
      subText={
        indicatorSet.finance_solutions.length ? (
          t('gfcr.forms.revenues.select_add_revenue')
        ) : (
          <Trans
            i18nKey="gfcr.forms.revenues.add_before_revenues"
            components={{
              financeSolutionLink: (
                <ButtonThatLooksLikeLinkUnderlined
                  type="button"
                  onClick={() => setSelectedNavItem('finance-solutions')}
                />
              ),
            }}
          />
        )
      }
    />
  )

  return (
    <>
      <TableContentToolbar>
        <ToolBarRow>
          <FilterSearchToolbar
            name={t('filters.by_revenue')}
            disabled={revenues.length === 0}
            globalSearchText={globalFilter || ''}
            handleGlobalFilterChange={handleGlobalFilterChange}
          />
          <ToolbarButtonWrapper>{toolbarButtons}</ToolbarButtonWrapper>
        </ToolBarRow>
      </TableContentToolbar>
      <StyledTableContentWrapper>{table}</StyledTableContentWrapper>
      <RevenueModal
        isOpen={isModalOpen}
        revenue={revenueBeingEdited}
        financeSolutions={nonPcfFinanceSolutions}
        choices={choices}
        onDismiss={handleRevenueModalDismiss}
        indicatorSet={indicatorSet}
        setIndicatorSet={setIndicatorSet}
        displayHelp={displayHelp}
      />
    </>
  )
}

Revenues.propTypes = {
  indicatorSet: PropTypes.object.isRequired,
  setIndicatorSet: PropTypes.func.isRequired,
  choices: choicesPropType.isRequired,
  setSelectedNavItem: PropTypes.func.isRequired,
  displayHelp: PropTypes.bool,
}

export default Revenues
